import * as THREE from 'three';
import { settings } from '@/config';

/**
 * The `Bright_Light_White_S_*` meshes — a glowing PORTAL light source (2026-07-17, user:
 * "that bright light mesh is like light … like it has emission applied on that … a portal
 * having glow").
 *
 * Two parts, because the scene has NO bloom to lean on (`settings.postprocessing.enabled`
 * is false — the composer's OutputPass re-tone-maps the baked atlases and ruins the baked
 * look, see the Round 10 notes). Without a bloom pass an "emissive" material is just a flat
 * bright surface, so the glow is built by hand:
 *
 *  1. {@link PortalLightMaterial} — the mesh's own surface: an unlit, `toneMapped:false`
 *     blown-out core (so it reads as a light SOURCE, not a lit object) with a Fresnel rim
 *     that brightens toward grazing angles, plus a slow breathing pulse.
 *  2. {@link createPortalHaloMaterial} — an additive shell mesh, a slightly scaled copy of
 *     the same geometry, that fades OUT from the silhouette. This is what actually spills
 *     light past the mesh's edge and sells "a light is coming from that".
 *
 * All tunables live in `settings.portalLight`.
 */

const VERTEX_SHADER = /* glsl */ `
  varying vec3 vWorldPos;
  varying vec3 vNormalW;
  varying vec2 vUv;
  void main() {
    vec4 wp = modelMatrix * vec4(position, 1.0);
    vWorldPos = wp.xyz;
    vNormalW = normalize(mat3(modelMatrix) * normal);
    // The portal quad is UV-mapped (verified against scene.glb: POSITION,NORMAL,TEXCOORD_0).
    // The uv attribute is declared unconditionally in three's ShaderMaterial vertex prefix, so
    // this is safe even for a geometry without it — it would simply read 0.
    vUv = uv;
    gl_Position = projectionMatrix * viewMatrix * wp;
  }
`;

const CORE_FRAGMENT = /* glsl */ `
  precision highp float;
  uniform vec3  uColor;
  uniform float uIntensity;
  uniform float uTime;
  uniform float uPulse;
  uniform float uPulseSpeed;
  uniform float uFresnelPower;
  uniform float uRimStrength;
  uniform float uFalloff;
  uniform float uOpacity;
  varying vec3 vWorldPos;
  varying vec3 vNormalW;
  varying vec2 vUv;

  void main() {
    vec3 V = normalize(cameraPosition - vWorldPos);
    vec3 N = normalize(vNormalW);
    // abs() so the portal reads the same from either face (the plane may be wound either way).
    float fres = pow(1.0 - clamp(abs(dot(V, N)), 0.0, 1.0), uFresnelPower);
    float pulse = 1.0 + uPulse * sin(uTime * uPulseSpeed);

    // RADIAL FALLOFF (2026-07-19). Previously this shader output a FLAT colour at alpha 1, so a
    // camera-facing quad rendered as a solid white rectangle that blocked the archway — the
    // Fresnel rim contributes nothing face-on (abs(dot(V,N)) == 1 ⇒ fres == 0), so there was
    // nothing to shape it. Fading from the centre out makes it read as light spilling through
    // the opening instead of a painted panel, and lets the arch and sky show through.
    float d = clamp(length(vUv - 0.5) * 2.0, 0.0, 1.0);
    float radial = pow(1.0 - d, uFalloff);

    float energy = radial * uIntensity * pulse * (1.0 + fres * uRimStrength);
    gl_FragColor = vec4(uColor * energy, energy * uOpacity);
  }
`;

const HALO_FRAGMENT = /* glsl */ `
  precision highp float;
  uniform vec3  uColor;
  uniform float uIntensity;
  uniform float uTime;
  uniform float uPulse;
  uniform float uPulseSpeed;
  uniform float uPower;
  varying vec3 vWorldPos;
  varying vec3 vNormalW;

  void main() {
    vec3 V = normalize(cameraPosition - vWorldPos);
    vec3 N = normalize(vNormalW);
    // Brightest where the shell turns away from the eye (its silhouette), fading to nothing
    // face-on — so it haloes the mesh's edge instead of washing a flat rectangle over it.
    float rim = pow(1.0 - clamp(abs(dot(V, N)), 0.0, 1.0), uPower);
    float pulse = 1.0 + uPulse * sin(uTime * uPulseSpeed);
    float energy = rim * uIntensity * pulse;
    gl_FragColor = vec4(uColor * energy, energy);
  }
`;

export class PortalLightMaterial extends THREE.ShaderMaterial {
  constructor() {
    const cfg = settings.portalLight;
    super({
      vertexShader: VERTEX_SHADER,
      fragmentShader: CORE_FRAGMENT,
      side: THREE.DoubleSide,
      // The light is emissive, not lit — keep it out of the tone-mapping curve so the core
      // stays blown-out white like a real light source (same rule as the baked atlases).
      toneMapped: false,
      fog: false,
      // ADDITIVE, not opaque: a light source adds to what's behind it. Opaque + alpha 1 is what
      // made this a white slab over the doorway. `depthWrite:false` so it never occludes the
      // architecture; `depthTest` stays on so the arch still occludes it correctly.
      transparent: true,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      uniforms: {
        uColor: { value: new THREE.Color(cfg.color) },
        uIntensity: { value: cfg.intensity },
        uTime: { value: 0 },
        uPulse: { value: cfg.pulse },
        uPulseSpeed: { value: cfg.pulseSpeed },
        uFresnelPower: { value: cfg.fresnelPower },
        uRimStrength: { value: cfg.rimStrength },
        uFalloff: { value: cfg.falloff },
        uOpacity: { value: cfg.opacity },
      },
    });
  }

  update(delta: number): void {
    this.uniforms.uTime!.value += delta;
  }

  applySettings(): void {
    const cfg = settings.portalLight;
    (this.uniforms.uColor!.value as THREE.Color).set(cfg.color);
    this.uniforms.uIntensity!.value = cfg.intensity;
    this.uniforms.uPulse!.value = cfg.pulse;
    this.uniforms.uPulseSpeed!.value = cfg.pulseSpeed;
    this.uniforms.uFresnelPower!.value = cfg.fresnelPower;
    this.uniforms.uRimStrength!.value = cfg.rimStrength;
    this.uniforms.uFalloff!.value = cfg.falloff;
    this.uniforms.uOpacity!.value = cfg.opacity;
  }
}

/** The additive halo shell material (see the file header). Shares the pulse so the halo
 *  breathes in step with the core. */
export function createPortalHaloMaterial(): THREE.ShaderMaterial {
  const cfg = settings.portalLight;
  return new THREE.ShaderMaterial({
    vertexShader: VERTEX_SHADER,
    fragmentShader: HALO_FRAGMENT,
    transparent: true,
    depthWrite: false, // additive glow must never occlude anything behind it
    depthTest: true, // but the architecture still occludes the halo correctly
    blending: THREE.AdditiveBlending,
    side: THREE.DoubleSide,
    toneMapped: false,
    fog: false,
    uniforms: {
      uColor: { value: new THREE.Color(cfg.halo.color) },
      uIntensity: { value: cfg.halo.intensity },
      uTime: { value: 0 },
      uPulse: { value: cfg.pulse },
      uPulseSpeed: { value: cfg.pulseSpeed },
      uPower: { value: cfg.halo.power },
    },
  });
}

/** Push live `settings.portalLight.halo` values into a halo material (debug GUI). */
export function applyHaloSettings(mat: THREE.ShaderMaterial): void {
  const cfg = settings.portalLight;
  (mat.uniforms.uColor!.value as THREE.Color).set(cfg.halo.color);
  mat.uniforms.uIntensity!.value = cfg.halo.intensity;
  mat.uniforms.uPower!.value = cfg.halo.power;
  mat.uniforms.uPulse!.value = cfg.pulse;
  mat.uniforms.uPulseSpeed!.value = cfg.pulseSpeed;
}
